import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, Modal, TouchableOpacity } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import Checkbox from 'expo-checkbox';
import { FontAwesome5 } from '@expo/vector-icons';
import BasicModalContainer from '../../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../../common/components/basicmodal/ModalFooter';
import { commonModalStyle } from '../../../../../common/components/basicmodal';
import AlertBanner from '../../../../../common/components/ui/AlertBanner';
import { getBrandsData } from '../../../../../api/Price';
import { getProductCategoriesData, getProductFamiliesData } from '../../../../../api/Product';
import { postAPICall } from '../../../../../api/BaseAPI';

// ── Types ──
interface DurationCol {
  key: string;            // "hourly_2", "daily_1" etc
  duration_type: string;
  duration_value: number;
  label: string;
}

// Cell = one price entry per fulfillment_type + duration combo
interface CellData {
  id: number | null;      // backend id (null = new)
  price: string;
  miles: string;
  battery_percent: string;
  _original_price?: string;
}

const FULFILLMENT_TYPES = ['Pickup', 'Delivery'];

// 'Extra Day' is stored as duration_type='daily' with duration_value=0 (a value no
// real tier can have) so it needs NO schema change. It is not a bookable duration:
// it defines the per-day price charged beyond the largest tier.
const isExtraDayCol = (type: string, value: number) => type === 'daily' && Number(value) === 0;

const getDurationLabel = (type: string, value: number) => {
  if (isExtraDayCol(type, value)) return 'Extra Day';
  switch (type) {
    case 'hourly': return `${value} Hour(s)`;
    case 'daily': return value === 1 ? '1 Day' : `${value} Days`;
    case 'weekly': return value === 1 ? '1 Week' : `${value} Weeks`;
    case 'monthly': return value === 1 ? '1 Month' : `${value} Months`;
    default: return `${value}`;
  }
};

const DURATION_ORDER = { hourly: 1, daily: 2, weekly: 3, monthly: 4 };

const sortColumns = (cols: DurationCol[]) =>
  [...cols].sort((a, b) => {
    // Extra Day always sorts last — it's a surcharge, not a tier.
    const xA = isExtraDayCol(a.duration_type, a.duration_value) ? 1 : 0;
    const xB = isExtraDayCol(b.duration_type, b.duration_value) ? 1 : 0;
    if (xA !== xB) return xA - xB;
    const dA = DURATION_ORDER[a.duration_type] || 5;
    const dB = DURATION_ORDER[b.duration_type] || 5;
    if (dA !== dB) return dA - dB;
    return a.duration_value - b.duration_value;
  });

const getDurationValueOptions = (durationType: string) => {
  switch (durationType) {
    case 'hourly': return Array.from({ length: 24 }, (_, i) => i + 1);
    case 'daily': return Array.from({ length: 21 }, (_, i) => i + 1);
    case 'weekly': return Array.from({ length: 20 }, (_, i) => i + 1);
    case 'monthly': return Array.from({ length: 12 }, (_, i) => i + 1);
    default: return [];
  }
};

const parseBrandIds = (brandIds: any): number[] => {
  if (!brandIds) return [];
  if (Array.isArray(brandIds)) return brandIds;
  if (typeof brandIds === 'string') {
    try { return JSON.parse(brandIds); } catch { return []; }
  }
  return [];
};

const makeDurationKey = (type: string, value: number) => `${type}_${value}`;

// ── Component ──
const AddVRPriceModal = ({ isModalVisible, group, closeModal, onSave, existingData = [], brands: parentBrands = [] }) => {
  const [localBrands, setLocalBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [families, setFamilies] = useState<any[]>([]);
  const [associatedBrandIds, setAssociatedBrandIds] = useState<number[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Group-level fields
  const [categoryId, setCategoryId] = useState('');
  const [familyId, setFamilyId] = useState(''); // used in edit mode only
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<number[]>([]); // used in add mode

  // Horizontal grid state
  const [columns, setColumns] = useState<DurationCol[]>([]);
  // cells[fulfillment_type][durationKey] = CellData
  const [cells, setCells] = useState<Record<string, Record<string, CellData>>>({});
  // Track which cells existed originally (for dirty/delete detection)
  const [originalCells, setOriginalCells] = useState<Record<string, Record<string, CellData>>>({});
  // Track duplicate entry IDs to clean up on save
  const [duplicateEntryIds, setDuplicateEntryIds] = useState<number[]>([]);

  // Add duration form
  const [addDurationType, setAddDurationType] = useState('hourly');
  const [addDurationValue, setAddDurationValue] = useState('');

  const brands = parentBrands.length > 0 ? parentBrands : localBrands;
  const isEditMode = !!group;

  // ── Load data on open ──
  useEffect(() => {
    if (!isModalVisible) return;

    // Reset alert state when modal opens
    setShowAlert(false);
    setAlertMessage('');

    const loadData = async () => {
      const [brandsData, categoriesData] = await Promise.all([
        parentBrands.length > 0
          ? Promise.resolve(parentBrands)
          : new Promise<any[]>((resolve) => {
              getBrandsData((jsonRes, status) => resolve(status === 200 && jsonRes ? jsonRes : []));
            }),
        new Promise<any[]>((resolve) => {
          getProductCategoriesData((jsonRes, status) => resolve(status === 200 && jsonRes ? jsonRes : []));
        }),
      ]);
      if (parentBrands.length === 0) setLocalBrands(brandsData);
      setCategories(categoriesData);

      if (group) {
        // Edit mode
        const catId = group.category_id?.toString() || '';
        setCategoryId(catId);
        setFamilyId(group.family_id?.toString() || '');
        setSelectedFamilyIds([]);
        setAssociatedBrandIds(group.brand_ids || []);

        if (catId) {
          getProductFamiliesData(catId, (jsonRes, status) => {
            if (status === 200 && jsonRes) setFamilies(jsonRes);
          });
        }

        // Build columns + cells from existing entries
        // Handle duplicates: keep latest (highest id), mark older ones for deletion
        const colMap: Record<string, DurationCol> = {};
        const cellMap: Record<string, Record<string, CellData>> = { Pickup: {}, Delivery: {} };
        const dupeIds: number[] = [];

        // Sort entries by id ascending so latest overwrites
        const sortedEntries = [...group.entries].sort((a, b) => a.id - b.id);

        for (const entry of sortedEntries) {
          const dKey = makeDurationKey(entry.duration_type, entry.duration_value);
          if (!colMap[dKey]) {
            colMap[dKey] = {
              key: dKey,
              duration_type: entry.duration_type,
              duration_value: entry.duration_value,
              label: entry.duration_label || getDurationLabel(entry.duration_type, entry.duration_value),
            };
          }
          if (!cellMap[entry.fulfillment_type]) cellMap[entry.fulfillment_type] = {};
          // If cell already exists for this ft+duration, the previous one is a duplicate
          const existingCell = cellMap[entry.fulfillment_type][dKey];
          if (existingCell && existingCell.id) {
            dupeIds.push(existingCell.id);
          }
          cellMap[entry.fulfillment_type][dKey] = {
            id: entry.id,
            price: entry.price?.toString() || '',
            miles: entry.miles?.toString() || '',
            battery_percent: entry.battery_percent?.toString() || '',
            _original_price: entry.price?.toString() || '',
          };
        }

        setDuplicateEntryIds(dupeIds);
        setColumns(sortColumns(Object.values(colMap)));
        setCells(cellMap);
        setOriginalCells(JSON.parse(JSON.stringify(cellMap)));
      } else {
        // New mode
        setCategoryId('');
        setFamilyId('');
        setSelectedFamilyIds([]);
        setAssociatedBrandIds([]);
        setFamilies([]);
        setColumns([]);
        setCells({ Pickup: {}, Delivery: {} });
        setOriginalCells({ Pickup: {}, Delivery: {} });
        setDuplicateEntryIds([]);
      }
      setAddDurationType('hourly');
      setAddDurationValue('');
    };
    loadData();
  }, [isModalVisible, group]);

  // ── Handlers ──
  const handleCategoryChange = (value: string) => {
    setCategoryId(value);
    setFamilyId('');
    setSelectedFamilyIds([]);
    setFamilies([]);
    
    if (!value) {
      setColumns([]);
      setCells({ Pickup: {}, Delivery: {} });
      return;
    }

    // Fetch families — always start fresh empty grid in Add mode (no prefetch)
    getProductFamiliesData(value, (jsonRes, status) => {
      if (status === 200 && jsonRes) {
        setFamilies(jsonRes);
        setSelectedFamilyIds(jsonRes.map((f: any) => f.id));
      }
      setColumns([]);
      setCells({ Pickup: {}, Delivery: {} });

      // Scan existingData for duplicate slots in this category and surface them
      // A duplicate means same brand_ids + family + fulfillment + duration (NOT across different brand groups)
      const catEntries = existingData.filter((e: any) => e.category_id === parseInt(value));
      const seen: Record<string, number> = {}; // slotKey -> first entry id
      const dupeIds: number[] = [];
      for (const e of catEntries) {
        const brandKey = [...parseBrandIds(e.brand_ids)].sort((a, b) => a - b).join(',');
        const slotKey = `${brandKey}_${e.family_id}_${e.fulfillment_type}_${e.duration_type}_${e.duration_value}`;
        if (seen[slotKey] !== undefined) {
          dupeIds.push(e.id);
        } else {
          seen[slotKey] = e.id;
        }
      }
      setDuplicateEntryIds(dupeIds);
    });
  };

  const handleFamilyToggle = (familyId: number) => {
    setSelectedFamilyIds(prev =>
      prev.includes(familyId) ? prev.filter(id => id !== familyId) : [...prev, familyId]
    );
  };

  const selectAllFamilies = () => setSelectedFamilyIds(families.map(f => f.id));
  const deselectAllFamilies = () => setSelectedFamilyIds([]);

  const handleBrandSelection = (brandId: number) => {
    setAssociatedBrandIds(prev =>
      prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]
    );
  };

  const selectAllBrands = () => setAssociatedBrandIds(brands.map(b => b.id));
  const deselectAllBrands = () => setAssociatedBrandIds([]);

  // ── Add duration column ──
  const addDurationColumn = () => {
    // 'Extra Day' needs no value: it maps to the daily_0 sentinel column.
    const isExtra = addDurationType === 'extra_day';
    if (!isExtra && !addDurationValue) return;
    const val = isExtra ? 0 : parseInt(addDurationValue);
    const type = isExtra ? 'daily' : addDurationType;
    const key = makeDurationKey(type, val);
    const label = getDurationLabel(type, val);

    // Prevent adding if column already exists in the grid
    if (columns.find(c => c.key === key)) {
      setAlertMessage(`Duration "${label}" already exists in the grid.`);
      setShowAlert(true);
      return;
    }

    const newCol: DurationCol = {
      key,
      duration_type: type,
      duration_value: val,
      label,
    };

    setColumns(prev => sortColumns([...prev, newCol]));
    setCells(prev => {
      const updated = { ...prev };
      for (const ft of FULFILLMENT_TYPES) {
        if (!updated[ft]) updated[ft] = {};
        updated[ft][key] = { id: null, price: '', miles: '', battery_percent: '' };
      }
      return updated;
    });
    setAddDurationValue('');
  };

  // ── Reload modal data after delete ──
  const reloadModalData = () => {
    if (isEditMode && group) {
      // Reload from group entries (which should be refreshed by parent)
      const colMap: Record<string, DurationCol> = {};
      const cellMap: Record<string, Record<string, CellData>> = { Pickup: {}, Delivery: {} };
      const dupeIds: number[] = [];

      const sortedEntries = [...group.entries].sort((a, b) => a.id - b.id);

      for (const entry of sortedEntries) {
        const dKey = makeDurationKey(entry.duration_type, entry.duration_value);
        if (!colMap[dKey]) {
          colMap[dKey] = {
            key: dKey,
            duration_type: entry.duration_type,
            duration_value: entry.duration_value,
            label: entry.duration_label || getDurationLabel(entry.duration_type, entry.duration_value),
          };
        }
        if (!cellMap[entry.fulfillment_type]) cellMap[entry.fulfillment_type] = {};
        const existingCell = cellMap[entry.fulfillment_type][dKey];
        if (existingCell && existingCell.id) {
          dupeIds.push(existingCell.id);
        }
        cellMap[entry.fulfillment_type][dKey] = {
          id: entry.id,
          price: entry.price?.toString() || '',
          miles: entry.miles?.toString() || '',
          battery_percent: entry.battery_percent?.toString() || '',
          _original_price: entry.price?.toString() || '',
        };
      }

      setDuplicateEntryIds(dupeIds);
      setColumns(sortColumns(Object.values(colMap)));
      setCells(cellMap);
      setOriginalCells(JSON.parse(JSON.stringify(cellMap)));
    }
  };

  // ── Remove duration column ──
  const removeDurationColumn = async (colKey: string) => {
    // Collect IDs of entries to delete
    const idsToDelete: number[] = [];
    for (const ft of FULFILLMENT_TYPES) {
      const cell = cells[ft]?.[colKey];
      if (cell?.id) {
        idsToDelete.push(cell.id);
      }
    }
    
    // If there are backend entries, delete them immediately
    if (idsToDelete.length > 0) {
      const deletePromises = idsToDelete.map(id => 
        new Promise<void>((resolve) => {
          postAPICall('vr/prices/delete', { id }, () => resolve());
        })
      );
      await Promise.all(deletePromises);
      
      // Notify parent to refresh data (but don't close modal)
      onSave({ creates: [], updates: [], deletes: idsToDelete });
      
      // Wait a bit for parent to refresh, then reload modal data
      setTimeout(() => {
        reloadModalData();
      }, 300);
    } else {
      // No backend entries, just remove from UI
      setColumns(prev => prev.filter(c => c.key !== colKey));
      setCells(prev => {
        const updated = { ...prev };
        for (const ft of FULFILLMENT_TYPES) {
          if (updated[ft]) {
            const { [colKey]: _, ...rest } = updated[ft];
            updated[ft] = rest;
          }
        }
        return updated;
      });
    }
  };

  // ── Update cell price ──
  const updateCellPrice = (ft: string, colKey: string, price: string) => {
    setCells(prev => ({
      ...prev,
      [ft]: {
        ...prev[ft],
        [colKey]: { ...(prev[ft]?.[colKey] || { id: null, miles: '', battery_percent: '' }), price },
      },
    }));
  };

  // ── Delete Pricing Group ──
  const handleDeletePricingGroup = () => {
    if (!categoryId) {
      setAlertMessage('Please select a Category to delete.');
      setShowAlert(true);
      return;
    }

    const categoryName = categories.find(c => c.id.toString() === categoryId)?.category || 'this category';
    const sortedGroupBrands = [...associatedBrandIds].sort((a, b) => a - b).join(',');
    const brandLabel = associatedBrandIds.length > 0
      ? brands.filter((b: any) => associatedBrandIds.includes(b.id)).map((b: any) => b.brand).join(', ')
      : 'this group';

    const confirmDelete = window.confirm(
      `Are you sure you want to delete pricing for "${categoryName}" — [${brandLabel}]?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    // Only delete entries belonging to this specific group (same category + same brand_ids)
    const groupEntries = existingData.filter((e: any) => {
      const entryBrands = [...parseBrandIds(e.brand_ids)].sort((a, b) => a - b).join(',');
      return e.category_id === parseInt(categoryId) && entryBrands === sortedGroupBrands;
    });

    if (groupEntries.length === 0) {
      setAlertMessage('No pricing found for this group.');
      setShowAlert(true);
      return;
    }

    const deleteIds = groupEntries.map((e: any) => e.id);
    onSave({ creates: [], updates: [], deletes: deleteIds });
    closeModal();
  };

  // ── Save ──
  const handleSave = () => {
    if (!categoryId) {
      setAlertMessage('Please select a Category.');
      setShowAlert(true);
      return;
    }
    if (associatedBrandIds.length === 0) {
      setAlertMessage('Please select at least one brand.');
      setShowAlert(true);
      return;
    }
    if (columns.length === 0) {
      setAlertMessage('Please add at least one duration.');
      setShowAlert(true);
      return;
    }

    // Check if at least one price is filled
    let hasPrices = false;
    const emptyColumns: string[] = [];
    
    for (const col of columns) {
      let colHasPrice = false;
      for (const ft of FULFILLMENT_TYPES) {
        const cell = cells[ft]?.[col.key];
        if (cell?.price && parseFloat(cell.price) > 0) {
          colHasPrice = true;
          hasPrices = true;
        }
      }
      if (!colHasPrice) {
        emptyColumns.push(col.label);
      }
    }
    
    if (!hasPrices) {
      setAlertMessage('Please enter at least one price before saving.');
      setShowAlert(true);
      return;
    }
    
    if (emptyColumns.length > 0) {
      setAlertMessage(`Warning: ${emptyColumns.join(', ')} ${emptyColumns.length === 1 ? 'has' : 'have'} no prices and will not be saved. Remove ${emptyColumns.length === 1 ? 'it' : 'them'} or add prices to continue.`);
      setShowAlert(true);
      return;
    }

    const selectedCategory = categories.find(c => c.id.toString() === categoryId);

    // In Add mode: check if any selected brand already has pricing in a DIFFERENT group for same category+duration
    if (!isEditMode) {
      const sortedNewBrands = [...associatedBrandIds].sort((a, b) => a - b).join(',');
      const conflicts: string[] = [];

      for (const col of columns) {
        for (const ft of FULFILLMENT_TYPES) {
          const cell = cells[ft]?.[col.key];
          if (!cell?.price || parseFloat(cell.price) <= 0) continue;

          // Find entries for same slot with DIFFERENT brand set
          const conflictingEntries = existingData.filter((e: any) =>
            e.category_id === parseInt(categoryId) &&
            e.fulfillment_type === ft &&
            e.duration_type === col.duration_type &&
            e.duration_value === col.duration_value &&
            [...parseBrandIds(e.brand_ids)].sort((a, b) => a - b).join(',') !== sortedNewBrands
          );

          for (const conflict of conflictingEntries) {
            const conflictBrands = parseBrandIds(conflict.brand_ids);
            const overlappingBrands = associatedBrandIds.filter(id => conflictBrands.includes(id));
            if (overlappingBrands.length > 0) {
              const brandNames = overlappingBrands.map(id => {
                const b = brands.find((br: any) => br.id === id);
                return b ? b.brand : `Brand ${id}`;
              });
              const conflictGroupBrandNames = conflictBrands.map(id => {
                const b = brands.find((br: any) => br.id === id);
                return b ? b.brand : `Brand ${id}`;
              });
              conflicts.push(
                `"${brandNames.join(', ')}" already has ${col.label} pricing in group [${conflictGroupBrandNames.join(', ')}]. Uncheck "${brandNames.join(', ')}" or edit that group instead.`
              );
            }
          }
        }
      }

      if (conflicts.length > 0) {
        setAlertMessage(`Conflict: ${conflicts[0]}`);
        setShowAlert(true);
        return;
      }
    }

    const creates: any[] = [];
    const updates: any[] = [];
    const deletes: number[] = [];

    const baseData = {
      brand_ids: associatedBrandIds,
      category_id: parseInt(categoryId),
      category_name: selectedCategory?.category || '',
    };

    if (isEditMode) {
      // Edit mode: update/delete existing cells
      for (const ft of FULFILLMENT_TYPES) {
        for (const col of columns) {
          const cell = cells[ft]?.[col.key];
          if (!cell || !cell.price || parseFloat(cell.price) <= 0) continue;

          const rowData = {
            ...baseData,
            fulfillment_type: ft,
            duration_type: col.duration_type,
            duration_value: col.duration_value,
            duration_label: col.label,
            price: parseFloat(cell.price) || 0,
            miles: cell.miles ? parseInt(cell.miles) : null,
            battery_percent: cell.battery_percent ? parseInt(cell.battery_percent) : null,
          };

          if (cell.id) {
            updates.push({ ...rowData, id: cell.id });
          } else {
            const sortedBrands = [...associatedBrandIds].sort((a, b) => a - b).join(',');
            const existingMatch = existingData.find((e: any) =>
              e.category_id === parseInt(categoryId) &&
              e.fulfillment_type === ft &&
              e.duration_type === col.duration_type &&
              e.duration_value === col.duration_value &&
              [...parseBrandIds(e.brand_ids)].sort((a, b) => a - b).join(',') === sortedBrands
            );
            if (existingMatch) {
              updates.push({ ...rowData, id: existingMatch.id });
            } else {
              creates.push(rowData);
            }
          }
        }
      }

      // Find deleted entries
      for (const ft of FULFILLMENT_TYPES) {
        const origFt = originalCells[ft] || {};
        for (const [dKey, origCell] of Object.entries(origFt)) {
          if (!origCell.id) continue;
          const currentCell = cells[ft]?.[dKey];
          const colStillExists = columns.find(c => c.key === dKey);
          if (!colStillExists || !currentCell || !currentCell.price || parseFloat(currentCell.price) <= 0) {
            if (!deletes.includes(origCell.id)) deletes.push(origCell.id);
          }
        }
      }
    } else {
      // Add mode: single pass — no family looping
      const sortedNewBrands = [...associatedBrandIds].sort((a, b) => a - b).join(',');

      for (const ft of FULFILLMENT_TYPES) {
        for (const col of columns) {
          const cell = cells[ft]?.[col.key];
          if (!cell || !cell.price || parseFloat(cell.price) <= 0) continue;

          // All existing entries for this category+fulfillment+duration (any brand_ids)
          const allSlotEntries = existingData.filter((e: any) =>
            e.category_id === parseInt(categoryId) &&
            e.fulfillment_type === ft &&
            e.duration_type === col.duration_type &&
            e.duration_value === col.duration_value
          );

          // Strip our newly-claimed brands from other groups
          for (const other of allSlotEntries) {
            const otherSorted = [...parseBrandIds(other.brand_ids)].sort((a, b) => a - b).join(',');
            if (otherSorted === sortedNewBrands) continue;
            const otherBrands = parseBrandIds(other.brand_ids);
            const stripped = otherBrands.filter((id: number) => !associatedBrandIds.includes(id));
            if (stripped.length < otherBrands.length) {
              if (stripped.length === 0) {
                if (!deletes.includes(other.id)) deletes.push(other.id);
              } else {
                const alreadyHandled = updates.some(u => u.id === other.id) || deletes.includes(other.id);
                if (!alreadyHandled) updates.push({ ...other, brand_ids: stripped });
              }
            }
          }

          const rowData = {
            ...baseData,
            brand_ids: associatedBrandIds,
            fulfillment_type: ft,
            duration_type: col.duration_type,
            duration_value: col.duration_value,
            duration_label: col.label,
            price: parseFloat(cell.price) || 0,
            miles: cell.miles ? parseInt(cell.miles) : null,
            battery_percent: cell.battery_percent ? parseInt(cell.battery_percent) : null,
          };

          const matchingEntry = allSlotEntries.find((e: any) =>
            !deletes.includes(e.id) &&
            [...parseBrandIds(e.brand_ids)].sort((a, b) => a - b).join(',') === sortedNewBrands
          );

          if (matchingEntry) {
            const stripIdx = updates.findIndex(u => u.id === matchingEntry.id);
            if (stripIdx !== -1) updates.splice(stripIdx, 1);
            updates.push({ ...rowData, id: matchingEntry.id });
            for (const dup of allSlotEntries) {
              if (dup.id !== matchingEntry.id &&
                  [...parseBrandIds(dup.brand_ids)].sort((a, b) => a - b).join(',') === sortedNewBrands &&
                  !deletes.includes(dup.id)) {
                deletes.push(dup.id);
              }
            }
          } else {
            creates.push(rowData);
          }
        }
      }
    }

    onSave({ creates, updates, deletes });
  };

  const COL_W = 110;
  const LABEL_W = 120;

  const totalPricesSet = FULFILLMENT_TYPES.reduce((count, ft) =>
    count + columns.filter(col => cells[ft]?.[col.key]?.price && parseFloat(cells[ft][col.key].price) > 0).length
  , 0);

  return (
    <Modal animationType="none" transparent={true} visible={isModalVisible}>
      <BasicModalContainer contentStyle={{ width: '98%', maxWidth: 1400, height: '95%' }}>
        <ModalHeader label={isEditMode ? 'Manage Pricing Group' : 'New Pricing Group'} closeModal={closeModal} />
        <ModalBody>
          <ScrollView style={{ flex: 1 }}>
            {/* ── Section 1: Group Config ── */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Vehicle Configuration</Text>
              <View style={styles.configRow}>
                <View style={styles.configLeft}>
                  <View style={styles.row}>
                    <View style={styles.column}>
                      <Text style={styles.label}>Category</Text>
                      <Picker
                        selectedValue={categoryId}
                        onValueChange={handleCategoryChange}
                        style={styles.picker}
                        enabled={!isEditMode}
                      >
                        <Picker.Item label="Select Category..." value="" />
                        {categories.map((c) => (
                          <Picker.Item key={c.id} label={c.category} value={c.id.toString()} />
                        ))}
                      </Picker>
                    </View>
                    {/* Family: disabled checkboxes in edit mode, multi-select checklist in add mode */}
                    {isEditMode ? (
                      <View style={styles.familiesContainer}>
                        <View style={styles.brandsTitleRow}>
                          <Text style={styles.label}>
                            {`Families (${[...new Set((group?.entries || []).map((e: any) => e.family_id))].length})`}
                          </Text>
                        </View>
                        <ScrollView style={styles.familiesScroll} nestedScrollEnabled>
                          {[...new Map((group?.entries || []).map((e: any) => [e.family_id, e])).values()].map((e: any) => (
                            <View key={e.family_id} style={styles.familyItem}>
                              <Checkbox value={true} disabled={true} color="#2980b9" />
                              <Text style={[styles.brandLabel, styles.brandLabelSelected]}>
                                {families.find((f: any) => f.id === e.family_id)?.family || `Family ${e.family_id}`}
                              </Text>
                            </View>
                          ))}
                        </ScrollView>
                      </View>
                    ) : (
                      <View style={styles.familiesContainer}>
                        <View style={styles.brandsTitleRow}>
                          <Text style={styles.label}>
                            Families ({families.length}/{families.length})
                          </Text>
                        </View>
                        {families.length === 0 ? (
                          <Text style={styles.familiesPlaceholder}>
                            {categoryId ? 'No families found for this category.' : 'Select a category first.'}
                          </Text>
                        ) : (
                          <ScrollView style={styles.familiesScroll} nestedScrollEnabled>
                            {families.map((f) => (
                              <View
                                key={f.id}
                                style={styles.familyItem}
                              >
                                <Checkbox
                                  value={true}
                                  onValueChange={() => {}}
                                  color={'#2e96e1'}
                                  disabled={true}
                                />
                                <Text style={[styles.brandLabel, styles.brandLabelSelected]}>
                                  {f.family}
                                </Text>
                              </View>
                            ))}
                          </ScrollView>
                        )}
                      </View>
                    )}
                  </View>
                  {!isEditMode && duplicateEntryIds.length > 0 && (
                    <View style={styles.infoRow}>
                      <Text style={styles.infoText}>
                        <Text style={{ color: '#e67e22' }}>{`${duplicateEntryIds.length} duplicate(s) will be cleaned up on save`}</Text>
                      </Text>
                    </View>
                  )}
                </View>
                <View style={styles.configRight}>
                  <View style={styles.brandsTitleRow}>
                    <Text style={styles.brandsTitle}>
                      Associated Brands ({associatedBrandIds.length}/{brands.length})
                    </Text>
                    <View style={styles.brandsActions}>
                      <TouchableOpacity onPress={selectAllBrands}>
                        <Text style={styles.brandsActionText}>All</Text>
                      </TouchableOpacity>
                      <Text style={styles.brandsActionSep}>|</Text>
                      <TouchableOpacity onPress={deselectAllBrands}>
                        <Text style={styles.brandsActionText}>None</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                  <ScrollView style={styles.brandsScroll} nestedScrollEnabled>
                    <View style={styles.brandsGrid}>
                      {brands.map((brand) => (
                        <View key={brand.id} style={styles.brandItem}>
                          <Checkbox
                            value={associatedBrandIds.includes(brand.id)}
                            onValueChange={() => handleBrandSelection(brand.id)}
                            color={associatedBrandIds.includes(brand.id) ? '#2e96e1' : undefined}
                          />
                          <Text style={[
                            styles.brandLabel,
                            associatedBrandIds.includes(brand.id) && styles.brandLabelSelected
                          ]}>{brand.brand}</Text>
                        </View>
                      ))}
                    </View>
                  </ScrollView>
                </View>
              </View>
            </View>

            {/* ── Section 2: Pricing Grid ── (shown only when families are selected in add mode, or always in edit mode) */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Pricing Grid</Text>
              <View style={styles.addDurationRow}>
                <Text style={styles.addDurLabel}>Add Duration:</Text>
                <Picker
                  selectedValue={addDurationType}
                  onValueChange={(v) => { setAddDurationType(v); setAddDurationValue(''); }}
                  style={[styles.picker, { width: 150 }]}
                >
                  <Picker.Item label="Hourly" value="hourly" />
                  <Picker.Item label="Daily" value="daily" />
                  <Picker.Item label="Weekly" value="weekly" />
                  <Picker.Item label="Monthly" value="monthly" />
                  <Picker.Item label="Extra Day" value="extra_day" />
                </Picker>
                {addDurationType !== 'extra_day' && (
                  <Picker
                    selectedValue={addDurationValue}
                    onValueChange={(v) => setAddDurationValue(v)}
                    style={[styles.picker, { width: 130 }]}
                  >
                    <Picker.Item label="Value..." value="" />
                    {getDurationValueOptions(addDurationType).map((val) => {
                      const key = makeDurationKey(addDurationType, val);
                      const existsInGrid = columns.find(c => c.key === key);
                      
                      let label = `${val}`;
                      if (existsInGrid) label += ' (exists)';
                      
                      return (
                        <Picker.Item
                          key={val}
                          label={label}
                          value={val.toString()}
                        />
                      );
                    })}
                  </Picker>
                )}
                {addDurationType === 'extra_day' && (
                  <Text style={styles.extraDayHint}>Per-day price for each additional day beyond the largest tier</Text>
                )}
                <TouchableOpacity
                  style={[styles.addDurBtn, addDurationType !== 'extra_day' && !addDurationValue && styles.addDurBtnDisabled]}
                  onPress={addDurationColumn}
                  disabled={addDurationType !== 'extra_day' && !addDurationValue}
                >
                  <FontAwesome5 name="plus" size={11} color="#fff" />
                  <Text style={styles.addDurBtnText}>Add</Text>
                </TouchableOpacity>
              </View>

              {/* ── Horizontal Grid ── */}
              {columns.length === 0 ? (
                <View style={styles.emptyGrid}>
                  <Text style={styles.emptyGridText}>Add durations above to build the pricing grid.</Text>
                </View>
              ) : (
                <ScrollView horizontal showsHorizontalScrollIndicator={true}>
                  <View>
                    {/* Header row */}
                    <View style={styles.gridHeaderRow}>
                      <View style={[styles.gridLabelCell, { width: LABEL_W }]}>
                        <Text style={styles.gridHeaderText}>Fulfillment</Text>
                      </View>
                      {columns.map(col => (
                        <View key={col.key} style={[styles.gridHeaderCell, { width: COL_W }]}>
                          <TouchableOpacity
                            onPress={() => removeDurationColumn(col.key)}
                            style={styles.colDeleteBtn}
                          >
                            <FontAwesome5 name="times" size={11} color="#e74c3c" />
                          </TouchableOpacity>
                          <Text style={styles.gridHeaderText}>{col.label}</Text>
                        </View>
                      ))}
                    </View>

                    {/* Price rows per fulfillment type */}
                    {FULFILLMENT_TYPES.map((ft, idx) => (
                      <View key={ft} style={[styles.gridDataRow, idx % 2 === 0 ? styles.gridRowEven : styles.gridRowOdd]}>
                        <View style={[styles.gridLabelCell, { width: LABEL_W }]}>
                          <View style={[styles.ftBadge, ft === 'Pickup' ? styles.ftPickup : styles.ftDelivery]}>
                            <Text style={styles.ftBadgeText}>{ft}</Text>
                          </View>
                        </View>
                        {columns.map(col => {
                          const cell = cells[ft]?.[col.key];
                          return (
                            <View key={col.key} style={[styles.gridCell, { width: COL_W }]}>
                              <TextInput
                                style={styles.gridPriceInput}
                                value={cell?.price || ''}
                                onChangeText={(text) => {
                                  // Only allow numbers and decimal point
                                  const numericText = text.replace(/[^0-9.]/g, '');
                                  updateCellPrice(ft, col.key, numericText);
                                }}
                                placeholder="$"
                                keyboardType="numeric"
                                selectTextOnFocus
                              />
                            </View>
                          );
                        })}
                      </View>
                    ))}
                  </View>
                </ScrollView>
              )}
            </View>
          </ScrollView>
        </ModalBody>
        <ModalFooter>
          <View style={styles.footerRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.footerSummary}>
                {columns.length} duration{columns.length !== 1 ? 's' : ''}
                {' · '}{totalPricesSet} price{totalPricesSet !== 1 ? 's' : ''} set
                {' · '}{associatedBrandIds.length} brand{associatedBrandIds.length !== 1 ? 's' : ''}
                {!isEditMode && (
                  <Text style={{ color: selectedFamilyIds.length > 0 ? '#27ae60' : '#e74c3c' }}>
                    {` · ${selectedFamilyIds.length} famil${selectedFamilyIds.length !== 1 ? 'ies' : 'y'} selected`}
                  </Text>
                )}
                {!isEditMode && duplicateEntryIds.length > 0 && (
                  <Text style={{ color: '#e67e22' }}>{` · ${duplicateEntryIds.length} dupe(s) to clean`}</Text>
                )}
                {showAlert && (
                  <Text style={{ color: '#e74c3c', fontWeight: '600' }}>{` · ${alertMessage}`}</Text>
                )}
              </Text>
            </View>
            <View style={styles.footerButtons}>
              {categoryId && (
                <TouchableOpacity onPress={handleDeletePricingGroup} style={styles.deleteButton}>
                  <Text style={styles.deleteButtonText}>Delete Pricing Group</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity onPress={handleSave}>
                <Text style={styles.addButton}>Save All</Text>
              </TouchableOpacity>
            </View>
          </View>
        </ModalFooter>
      </BasicModalContainer>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ...commonModalStyle,
  // ── Sections ──
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#333',
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingBottom: 6,
  },
  // ── Config ──
  configRow: {
    flexDirection: 'row',
    gap: 20,
  },
  configLeft: {
    flex: 1,
  },
  configRight: {
    width: 300,
    borderLeftWidth: 1,
    borderLeftColor: '#e0e0e0',
    paddingLeft: 20,
  },
  brandsTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  brandsTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#333',
  },
  brandsActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  brandsActionText: {
    fontSize: 12,
    color: '#2e96e1',
    fontWeight: '600',
  },
  brandsActionSep: {
    fontSize: 12,
    color: '#ccc',
  },
  brandsScroll: {
    maxHeight: 280,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 4,
    padding: 6,
    backgroundColor: '#fafafa',
  },
  brandsGrid: {
    flexDirection: 'column',
  },
  brandItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 3,
    paddingVertical: 2,
  },
  brandLabel: {
    marginLeft: 8,
    fontSize: 13,
    color: '#555',
  },
  brandLabelSelected: {
    color: '#333',
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 10,
  },
  column: {
    flex: 1,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 4,
    color: '#555',
  },
  picker: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    padding: 8,
    height: 38,
  },
  infoRow: {
    marginTop: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: '#f0f7ff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#d0e3f7',
  },
  infoText: {
    fontSize: 12,
    color: '#555',
  },
  // ── Add Duration ──
  addDurationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  addDurLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#555',
  },
  addDurBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: '#2e96e1',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 4,
  },
  addDurBtnDisabled: {
    backgroundColor: '#aaa',
  },
  addDurBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  extraDayHint: {
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
    maxWidth: 320,
  },
  // ── Grid ──
  emptyGrid: {
    padding: 30,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    backgroundColor: '#fafafa',
  },
  emptyGridText: {
    fontSize: 13,
    color: '#999',
  },
  gridHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#ccc',
    backgroundColor: '#eef2f7',
  },
  gridLabelCell: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    justifyContent: 'center',
  },
  gridHeaderCell: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: '#ddd',
  },
  gridHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#555',
    textAlign: 'center',
  },
  colDeleteBtn: {
    position: 'absolute',
    top: 2,
    right: 4,
    padding: 3,
    zIndex: 1,
  },
  gridDataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  gridRowEven: {
    backgroundColor: '#fff',
  },
  gridRowOdd: {
    backgroundColor: '#fafbfc',
  },
  gridCell: {
    paddingVertical: 5,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: '#eee',
  },
  gridPriceInput: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    color: '#333',
    width: '100%',
    height: 34,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 3,
    backgroundColor: '#fff',
    paddingHorizontal: 4,
  },
  // ── Fulfillment badges ──
  ftBadge: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
  },
  ftPickup: {
    backgroundColor: '#e8f5e9',
  },
  ftDelivery: {
    backgroundColor: '#fff3e0',
  },
  ftBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
  },
  // ── Footer ──
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  deleteButton: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  familiesContainer: {
    minWidth: 220,
    maxWidth: 320,
  },
  familiesScroll: {
    maxHeight: 130,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 6,
    backgroundColor: '#fafafa',
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  familyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    gap: 8,
  },
  familiesPlaceholder: {
    fontSize: 13,
    color: '#999',
    fontStyle: 'italic',
    paddingVertical: 8,
  },
  footerSummary: {
    fontSize: 13,
    color: '#777',
  },
});

export default AddVRPriceModal;
